import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Youtube, Send, Phone, Mail } from "lucide-react";
import Logo from "./Logo";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { CALL_NUMBER_DISPLAY, CALL_NUMBER_TEL, SUPPORT_EMAIL } from "../lib/contact";

const COLS = [
    {
        title: "Explore",
        links: [
            ["Services", "/services"], ["Courses", "/courses"], ["Portfolio", "/portfolio"], ["Pricing", "/pricing"],
        ],
    },
    {
        title: "Company",
        links: [
            ["About Us", "/about"], ["Resources", "/resources"], ["Blog", "/blog"], ["Contact", "/about"], ["Privacy & Policies", "/privacy"],
        ],
    },
];

export default function Footer() {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSending(true);
        try {
            await api.post("/contact", { name: "Newsletter", email, message: "Newsletter signup", type: "newsletter" });
            toast.success("You're on the list.");
            setEmail("");
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    return (
        <footer className="bg-navy text-white grain" data-testid="site-footer">
            <div className="mx-container py-16 lg:py-24">
                <div className="grid lg:grid-cols-12 gap-12 text-center lg:text-left">
                    <div className="lg:col-span-5 flex flex-col items-center lg:items-start">
                        <Logo theme="dark" size="lg" withTagline />
                        <p className="mt-6 max-w-sm text-white/60 leading-relaxed">
                            Everything independent artists need to create, release, build and grow. Production, visuals,
                            branding, marketing and distribution.
                        </p>
                        <form onSubmit={subscribe} className="mt-8 flex w-full max-w-sm border-b border-white/20 focus-within:border-brand-yellow transition-colors">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email"
                                data-testid="footer-newsletter-input"
                                className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-white/40 outline-none text-center sm:text-left"
                            />
                            <button disabled={sending} className="text-brand-yellow px-3" data-testid="footer-newsletter-submit" aria-label="Subscribe">
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </div>

                    {COLS.map((c) => (
                        <div key={c.title} className="lg:col-span-2">
                            <h4 className="overline !text-white/40 mb-5">{c.title}</h4>
                            <ul className="space-y-3">
                                {c.links.map(([label, to]) => (
                                    <li key={label}>
                                        <Link to={to} className="text-white/70 hover:text-brand-yellow transition-colors text-sm">
                                            {label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="lg:col-span-3 flex flex-col items-center lg:items-start">
                        <h4 className="overline !text-white/40 mb-5">Get in touch</h4>
                        <a href={CALL_NUMBER_TEL} className="text-white/70 hover:text-brand-yellow text-sm inline-flex items-center gap-2 mb-3" data-testid="footer-call">
                            <Phone className="h-4 w-4" /> {CALL_NUMBER_DISPLAY}
                        </a>
                        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-white/70 hover:text-brand-yellow text-sm inline-flex items-center gap-2 mb-4">
                            <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
                        </a>
                        <div className="flex gap-3 justify-center lg:justify-start">
                            {[Instagram, Youtube].map((Icon, i) => (
                                <a key={i} href="#" className="h-10 w-10 rounded-full border border-white/15 grid place-items-center hover:border-brand-yellow hover:text-brand-yellow transition-colors" aria-label="social">
                                    <Icon className="h-4 w-4" />
                                </a>
                            ))}
                        </div>
                        <Link to="/signup" className="mt-8 inline-flex items-center gap-1.5 text-brand-yellow font-semibold text-sm uppercase tracking-wide">
                            Start your journey <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40 text-center">
                    <span>© {new Date().getFullYear()} MUVIARTS. All rights reserved.</span>
                    <span>Premium creative agency · Music · Artist development</span>
                </div>
            </div>
        </footer>
    );
}
